import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Badge } from './Badge';
import { Button } from './Button';
import { TextArea } from './TextArea';

/**
 * BadRatingDiagnosisCard
 *
 * AI root-cause diagnosis of a bad rating, shown to an admin/doc owner.
 * Layout (design direction B): a tinted severity HEADER band (AI attribution +
 * category + confidence) over a NEUTRAL body (explanation, action, documents,
 * collapsible evidence). Colors come from theme.css semantic tokens only.
 *
 * Rationale (AI-insight best practices): severity is signalled with icon + text
 * + a contained color accent — not a full-bleed tint — so it reads as a
 * trustworthy insight, not an alarm. Reasoning leads; details are progressively
 * disclosed.
 */

// Category -> visual tone + icon.
const CATEGORY_CONFIG = {
  missing_doc: { tone: 'warning', icon: 'lucide:file-x' },
  contradictory_docs: { tone: 'warning', icon: 'lucide:split' },
  outdated_doc: { tone: 'warning', icon: 'lucide:calendar-clock' },
  doc_mismatch_reality: { tone: 'warning', icon: 'lucide:file-question' },
  retrieval_miss: { tone: 'info', icon: 'lucide:search-x' },
  hallucination: { tone: 'error', icon: 'lucide:triangle-alert' },
  ambiguous_question: { tone: 'muted', icon: 'lucide:message-circle-question' },
  correct_but_disagree: { tone: 'muted', icon: 'lucide:message-square' },
  other: { tone: 'muted', icon: 'lucide:info' },
};

// tone -> theme.css classes for the header band (tint) + accent text.
const TONE_CLASSES = {
  warning: { headerBg: 'bg-warning/10', border: 'border-warning/20', text: 'text-warning' },
  info: { headerBg: 'bg-info/10', border: 'border-info/20', text: 'text-info' },
  error: { headerBg: 'bg-error/10', border: 'border-error/20', text: 'text-error' },
  muted: { headerBg: 'bg-surface-alt', border: 'border-border-default', text: 'text-muted' },
};

const DEFAULT_CONFIG = { tone: 'muted', icon: 'lucide:info' };

export function BadRatingDiagnosisCard({ analysis, onMarkReview }) {
  const { t } = useTranslation();
  const [showEvidence, setShowEvidence] = useState(false);
  const [showReviewBox, setShowReviewBox] = useState(false);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!analysis) return null;

  if (analysis.status === 'error') {
    return (
      <div className="rounded-xl border border-border-default bg-surface p-4 text-sm leading-relaxed text-muted">
        {t('conversations.diagnosis.errorState', 'Could not analyze this rating automatically.')}
      </div>
    );
  }

  const config = CATEGORY_CONFIG[analysis.category] || DEFAULT_CONFIG;
  const tone = TONE_CLASSES[config.tone] || TONE_CLASSES.muted;
  const categoryLabel = t(`conversations.diagnosis.categories.${analysis.category}`, analysis.category);
  const confidencePct =
    typeof analysis.confidence === 'number' ? Math.round(analysis.confidence * 100) : null;
  const suspected = analysis.suspected_documents || [];

  const handleMarkNotConform = async () => {
    if (!onMarkReview) return;
    setSubmitting(true);
    try {
      await onMarkReview(true, comment);
      setShowReviewBox(false);
      setComment('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUndo = async () => {
    if (!onMarkReview) return;
    setSubmitting(true);
    try {
      await onMarkReview(false, '');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border-default bg-surface overflow-hidden">
      {/* Header band — tinted by severity */}
      <div className={`${tone.headerBg} border-b ${tone.border} px-4 pt-3 pb-3.5`}>
        <div className="flex items-center gap-1.5 mb-2">
          <Icon icon="lucide:sparkles" className={`size-3 ${tone.text} opacity-70`} />
          <span className={`text-[10px] font-semibold uppercase tracking-[0.09em] ${tone.text} opacity-70`}>
            {t('conversations.diagnosis.blockTitle', 'AI analysis of the answer')}
          </span>
          {analysis.needs_review && (
            <span className="ml-auto">
              <Badge variant="warning">
                {t('conversations.diagnosis.reviewFlagged', 'Flagged for review')}
              </Badge>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Icon icon={config.icon} className={`size-[18px] shrink-0 ${tone.text}`} />
          <span className={`text-[15px] font-semibold leading-snug ${tone.text}`}>{categoryLabel}</span>
          {confidencePct !== null && (
            <span className={`ml-auto shrink-0 text-xs font-medium tabular-nums ${tone.text} opacity-80`}>
              {t('conversations.diagnosis.confidence', 'Confidence')} {confidencePct}%
            </span>
          )}
        </div>
      </div>

      {/* Body — neutral */}
      <div className="p-4 space-y-4">
        {/* Explanation (the reasoning — leads) */}
        {analysis.explanation && (
          <p className="text-sm leading-relaxed text-default whitespace-pre-line">{analysis.explanation}</p>
        )}

        {/* Suggested action / note */}
        {analysis.suggested_action && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Icon
                icon={analysis.is_actionable ? 'lucide:wrench' : 'lucide:info'}
                className="size-3.5 text-subtle"
              />
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-label">
                {analysis.is_actionable
                  ? t('conversations.diagnosis.suggestedAction', 'Suggested action')
                  : t('conversations.diagnosis.note', 'Note')}
              </span>
            </div>
            <p className="text-sm leading-relaxed text-default whitespace-pre-line">{analysis.suggested_action}</p>
          </div>
        )}

        {/* Documents involved */}
        {suspected.length > 0 && (
          <div className="space-y-1.5">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-label">
              {t('conversations.diagnosis.suspectedDocuments', 'Documents involved')}
            </span>
            <ul className="space-y-1.5">
              {suspected.map((doc, idx) => (
                <li key={`${doc.name}-${idx}`} className="flex items-start gap-2 text-sm leading-relaxed text-muted">
                  <Icon icon="lucide:file-text" className="size-4 mt-0.5 text-subtle shrink-0" />
                  <span>
                    {doc.url ? (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor-pointer font-medium text-default hover:text-brand hover:underline underline-offset-2 inline-flex items-center gap-1"
                      >
                        {doc.name}
                        <Icon icon="lucide:external-link" className="size-3 shrink-0" />
                      </a>
                    ) : (
                      <span className="font-medium text-default">{doc.name}</span>
                    )}
                    {doc.reason && <span className="text-muted"> — {doc.reason}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Evidence — progressive disclosure */}
        {analysis.evidence && (
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => setShowEvidence((v) => !v)}
              className="cursor-pointer flex items-center gap-1 text-xs font-medium text-subtle hover:text-muted transition-colors"
            >
              <Icon
                icon={showEvidence ? 'lucide:chevron-down' : 'lucide:chevron-right'}
                className="size-3.5"
              />
              {t('conversations.diagnosis.evidence', 'How this was diagnosed')}
            </button>
            {showEvidence && (
              <p className="mt-2 text-xs leading-relaxed text-muted whitespace-pre-line border-l-2 border-border-default pl-3">
                {analysis.evidence}
              </p>
            )}
          </div>
        )}

        {/* "Not conform" — disagree with the AI diagnosis and flag it for the team */}
        {onMarkReview && (
          <div className="pt-1 border-t border-border-default">
            {analysis.not_conform ? (
              <div className="pt-3 space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs text-muted">
                  <Icon icon="lucide:flag" className="size-3.5 text-warning" />
                  <span className="font-medium">
                    {t('conversations.diagnosis.markedNotConform', 'Marked as not conform')}
                    {analysis.marked_by ? ` · ${analysis.marked_by}` : ''}
                  </span>
                </div>
                {analysis.review_comment && (
                  <p className="text-xs leading-relaxed text-muted whitespace-pre-line pl-5">
                    {analysis.review_comment}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleUndo}
                  disabled={submitting}
                  className="cursor-pointer pl-5 text-xs font-medium text-subtle hover:text-muted transition-colors disabled:opacity-50"
                >
                  {t('conversations.diagnosis.undoNotConform', 'Undo')}
                </button>
              </div>
            ) : showReviewBox ? (
              <div className="pt-3 space-y-2">
                <TextArea
                  placeholder={t('conversations.diagnosis.reviewComment', 'Why do you disagree? (optional)')}
                  value={comment}
                  onChange={(value) => setComment(value)}
                  rows={2}
                />
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={handleMarkNotConform} disabled={submitting}>
                    <Icon icon="lucide:flag" className="size-3.5" />
                    {t('conversations.diagnosis.markForReview', 'Flag for review')}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowReviewBox(false)}
                    disabled={submitting}
                    className="cursor-pointer text-xs font-medium text-subtle hover:text-muted transition-colors disabled:opacity-50"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowReviewBox(true)}
                className="cursor-pointer flex items-center gap-1.5 pt-3 text-xs font-medium text-subtle hover:text-muted transition-colors"
              >
                <Icon icon="lucide:thumbs-down" className="size-3.5" />
                {t('conversations.diagnosis.notConform', "I disagree with this analysis")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

BadRatingDiagnosisCard.propTypes = {
  analysis: PropTypes.shape({
    status: PropTypes.string,
    category: PropTypes.string,
    confidence: PropTypes.number,
    is_actionable: PropTypes.bool,
    explanation: PropTypes.string,
    suggested_action: PropTypes.string,
    evidence: PropTypes.string,
    suspected_documents: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        reason: PropTypes.string,
        url: PropTypes.string,
      })
    ),
    needs_review: PropTypes.bool,
    not_conform: PropTypes.bool,
    review_comment: PropTypes.string,
    marked_by: PropTypes.string,
  }),
  onMarkReview: PropTypes.func,
};
